import { useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion'
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2, Clock, Globe, Shield, Smartphone, Cloud, Layout, Briefcase, Zap, Users, MessageSquare, ChevronDown, Plus, Minus } from 'lucide-react'
import Seo from '../components/Seo.jsx'
import PhoneField from '../components/PhoneField.jsx'
import Container from '../components/Container.jsx'
import { site } from '../data/site.js'
import { services } from '../data/services.js'

const API_BASE = import.meta.env.VITE_API_BASE || '/api'

const EASE = [0.16, 1, 0.3, 1]

const servicesList = [
  { icon: Globe, title: 'Website Development', desc: 'Custom, high-performance web applications.' },
  { icon: Smartphone, title: 'Mobile App Development', desc: 'Native & cross-platform mobile solutions.' },
  { icon: Layout, title: 'UI/UX Design', desc: 'Premium, user-centric interface design.' },
  { icon: Cloud, title: 'Cloud Solutions', desc: 'Scalable cloud infrastructure & migration.' },
  { icon: Shield, title: 'Cyber Security', desc: 'Enterprise-grade security & monitoring.' },
  { icon: Zap, title: 'AI & Automation', desc: 'Intelligent process automation & AI integration.' },
]

const featuresList = [
  { icon: MessageSquare, title: 'Free Project Consultation', desc: 'Discuss your ideas with our experts at no cost.' },
  { icon: Users, title: 'Experienced Team', desc: 'Senior developers & designers dedicated to your success.' },
  { icon: Briefcase, title: 'Transparent Pricing', desc: 'Clear, milestone-based pricing with no hidden fees.' },
  { icon: Clock, title: 'Fast Project Delivery', desc: 'Agile methodologies ensuring rapid time-to-market.' },
]

const processSteps = [
  { num: '01', title: 'Book a Free Consultation', desc: 'Initial discovery call to understand your vision and goals.' },
  { num: '02', title: 'Requirement Analysis', desc: 'Deep dive into technical, functional, and business requirements.' },
  { num: '03', title: 'Proposal & Planning', desc: 'Detailed roadmap, precise timeline, and architecture blueprint.' },
  { num: '04', title: 'UI/UX Design', desc: 'Creating premium wireframes and interactive high-fidelity prototypes.' },
  { num: '05', title: 'Development', desc: 'Agile software engineering with regular transparent sprint updates.' },
  { num: '06', title: 'Testing & QA', desc: 'Rigorous quality assurance, performance optimization, and security testing.' },
  { num: '07', title: 'Deployment & Support', desc: 'Smooth launch, post-launch monitoring, and ongoing 24/7 maintenance.' },
]

const faqs = [
  { q: 'How soon can we start?', a: 'We typically onboard new clients within 3-5 business days after the initial consultation and proposal approval.' },
  { q: 'What industries do you work with?', a: 'We specialize in Healthcare, Finance, SaaS, E-commerce, and Enterprise IT solutions, but our tech expertise translates across all verticals.' },
  { q: 'Do you provide ongoing support?', a: 'Yes, we offer comprehensive 24/7 maintenance and support SLAs to ensure your systems run flawlessly post-launch.' },
  { q: 'Can you redesign an existing website or app?', a: 'Absolutely. We regularly audit and redesign legacy systems to meet modern premium standards for usability and performance.' },
  { q: 'How long does a project take?', a: 'Project timelines vary based on complexity. A typical corporate website takes 4-6 weeks, while complex web/mobile apps can take 3-6 months.' },
  { q: 'What technologies do you use?', a: 'We use modern, scalable tech stacks including React, Next.js, Node.js, Python, Flutter, AWS, and advanced AI/ML frameworks.' },
]

function FaqItem({ q, a, isOpen, onClick }) {
  return (
    <div className="border-b border-stone-200 last:border-0">
      <button
        onClick={onClick}
        className="flex w-full items-center justify-between py-6 text-left focus:outline-none"
      >
        <span className="font-display text-lg font-medium text-stone-900">{q}</span>
        <span className="ml-6 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-stone-100 text-cyan-600 transition-colors hover:bg-cyan-100">
          {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="pb-6 text-stone-600 leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default function Contact() {
  const [searchParams] = useSearchParams()
  const [form, setForm] = useState({ 
    name: '', email: '', phone: '', interest: searchParams.get('interest') || '', message: '' 
  })
  const [status, setStatus] = useState('idle')
  const [error, setError] = useState('')
  const [openFaq, setOpenFaq] = useState(0)

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }))

  const onSubmit = async (e) => {
    e.preventDefault()
    setStatus('submitting')
    setError('')
    try {
      const res = await fetch(`${API_BASE}/contact.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.success) {
        setStatus('success')
        setForm({ name: '', email: '', phone: '', interest: '', message: '' })
      } else {
        setStatus('error')
        setError(data.message || 'Something went wrong. Please try again.')
      }
    } catch {
      setStatus('error')
      setError('Could not reach the server. Please email us directly at ' + site.email + '.')
    }
  }

  const inputClass = "w-full rounded-xl border border-stone-200 bg-white px-4 py-3.5 text-[16px] text-stone-900 placeholder-stone-400 transition-all focus:border-cyan-500 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 shadow-sm sm:px-5 sm:py-4 sm:text-sm"

  return (
    <>
      <Seo
        title="Contact Us | Enterprise IT Solutions"
        description={`Get in touch with ${site.name} to start a project, request a quote, or ask about our enterprise IT services.`}
        path="/contact"
      />
      <div className="min-h-screen bg-stone-50 selection:bg-cyan-500/20">
        
        {/* =========================================
            1. HERO SECTION (BANNER)
        ========================================= */}
        {/* Top padding only clears the fixed navbar (its pill ends at ~74px) —
            anything more reads as dead space above the title. */}
        <section className="relative flex items-center justify-center overflow-hidden bg-slate-950 pt-[82px] md:pt-[119px] pb-3 sm:pb-8 lg:pb-12">
          <div className="absolute inset-0 z-0">
            <img src="/images/contact_hero.png" alt="Contact Us" className="h-full w-full object-cover object-center opacity-40 mix-blend-luminosity" />
            <div className="absolute inset-0 bg-slate-900/60" />
          </div>

          <Container className="relative z-10 text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
              className="font-display text-2xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl"
            >
              Contact Us
            </motion.h1>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
              className="mt-1 sm:mt-3 flex items-center justify-center space-x-2 text-[11px] font-bold tracking-wide text-slate-300 sm:text-base"
            >
              <a href="/" className="text-red-500 hover:text-red-400 transition-colors">Home</a>
              <span>/</span>
              <span className="text-white">Contact Us</span>
            </motion.div>
          </Container>
        </section>

        {/* =========================================
            2. CONTACT FORM & INFO SECTION
        ========================================= */}
        <section id="form" className="relative py-10 sm:py-20 lg:py-30">
          <Container>
            <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
              
              {/* Left Side: Form */}
              <div className="lg:col-span-7">
                <div className="relative overflow-hidden -mx-5 rounded-none border-y border-stone-200 bg-white px-5 py-9 sm:mx-0 sm:rounded-[2.5rem] sm:border sm:p-12 sm:shadow-xl">
                  <div className="absolute top-0 right-0 h-64 w-64 bg-cyan-400/5 blur-[100px] rounded-full pointer-events-none" />
                  
                  <h2 className="font-display text-2xl font-bold text-stone-900 mb-2 sm:text-3xl">Request A Quote</h2>
                  <p className="mb-7 text-sm leading-relaxed text-stone-500 sm:mb-8 sm:text-base">Fill out the form below and our team will get back to you within 24 hours.</p>

                  {status === 'success' ? (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="mb-6 rounded-full bg-emerald-100 p-4">
                        <CheckCircle2 className="h-16 w-16 text-emerald-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-stone-900">Message Sent Successfully!</h3>
                      <p className="mt-3 text-stone-600">Thank you for reaching out. A project manager will review your request and contact you shortly.</p>
                      <button onClick={() => setStatus('idle')} className="mt-8 rounded-full border border-stone-200 bg-stone-50 px-8 py-3 text-sm font-semibold text-stone-700 transition hover:bg-stone-100">
                        Send Another Message
                      </button>
                    </motion.div>
                  ) : (
                    <form onSubmit={onSubmit} className="relative z-10 space-y-6">
                      <div className="grid gap-6 sm:grid-cols-2">
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-stone-500">Full Name *</label>
                          <input name="name" value={form.name} onChange={update} required className={inputClass} placeholder="John Doe" />
                        </div>
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-stone-500">Phone Number</label>
                          <PhoneField
                            name="phone"
                            value={form.phone}
                            onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                            placeholder="Phone number"
                            ariaLabel="Phone number"
                          />
                        </div>
                      </div>

                      <div className="grid gap-6 sm:grid-cols-2">
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-stone-500">Email Address *</label>
                          <input type="email" name="email" value={form.email} onChange={update} required className={inputClass} placeholder="john@example.com" />
                        </div>
                        <div>
                          <label htmlFor="contact-interest" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-stone-500">Service Interested In *</label>
                          <select id="contact-interest" name="interest" value={form.interest} onChange={update} required className={inputClass}>
                            <option value="">Select a service</option>
                            {servicesList.map((s) => (
                              <option key={s.title} value={s.title}>{s.title}</option>
                            ))}
                            <option value="Other">Other / Not Sure</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-stone-500">Message *</label>
                        <textarea name="message" value={form.message} onChange={update} required rows={4} className={inputClass} placeholder="Tell us about your project requirements..." />
                      </div>

                      {status === 'error' && (
                        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">{error}</div>
                      )}

                      <button type="submit" disabled={status === 'submitting'} className="group relative w-full overflow-hidden rounded-full bg-cyan-500 px-8 py-4 text-[16px] font-bold text-white shadow-lg shadow-cyan-500/30 transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 sm:text-[15px]">
                        <span className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 opacity-0 transition-opacity group-hover:opacity-100" />
                        <span className="relative flex items-center justify-center gap-2">
                          {status === 'submitting' ? <><Loader2 className="h-5 w-5 animate-spin" /> Processing...</> : <><Send className="h-5 w-5" /> Send Message</>}
                        </span>
                      </button>
                    </form>
                  )}
                </div>
              </div>

              {/* Right Side: Contact Info & Map */}
              <div className="lg:col-span-5 flex flex-col gap-6">
                
                {/* Contact Cards Grid */}
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1">
                  <div className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm transition-shadow hover:shadow-md">
                    <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-cyan-50 text-cyan-600">
                      <Mail className="h-6 w-6" />
                    </div>
                    <h4 className="text-sm font-medium text-stone-500">Email Address</h4>
                    <a href={`mailto:${site.email}`} className="mt-1 block font-display text-lg text-stone-900 hover:text-cyan-600">{site.email}</a>
                  </div>

                  <div className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm transition-shadow hover:shadow-md">
                    <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-cyan-50 text-cyan-600">
                      <Phone className="h-6 w-6" />
                    </div>
                    <h4 className="text-sm font-medium text-stone-500">Phone Number</h4>
                    <a href={`tel:${site.phone}`} className="mt-1 block font-display text-lg text-stone-900 hover:text-cyan-600">{site.phone}</a>
                  </div>

                  <div className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm transition-shadow hover:shadow-md">
                    <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-cyan-50 text-cyan-600">
                      <Clock className="h-6 w-6" />
                    </div>
                    <h4 className="text-sm font-medium text-stone-500">Business Hours & Response Time</h4>
                    <p className="mt-1 font-display text-lg text-stone-900">Mon-Fri, 9AM-6PM (EST)</p>
                    <p className="mt-1 text-sm text-cyan-600">Support Response: Within 24 Hours</p>
                  </div>
                </div>
              </div>

            </div>
          </Container>
        </section>



        {/* =========================================
            5. FAQ SECTION
        ========================================= */}
        <section className="py-20 lg:py-32 bg-white border-y border-stone-200">
          <Container>
            <div className="mx-auto max-w-3xl">
              <div className="text-center mb-16">
                <h2 className="font-display text-4xl font-bold text-stone-900">Frequently Asked Questions</h2>
                <p className="mt-4 text-stone-500">Everything you need to know about working with us.</p>
              </div>
              <div className="rounded-[2rem] border border-stone-200 bg-stone-50 p-8 sm:p-12 shadow-sm">
                {faqs.map((faq, i) => (
                  <FaqItem
                    key={i}
                    q={faq.q}
                    a={faq.a}
                    isOpen={openFaq === i}
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  />
                ))}
              </div>
            </div>
          </Container>
        </section>


      </div>
    </>
  )
}
